/**
 * Common engine interface + shared types for all sauna builders.
 * Each engine (fyrkantig, tunna, tvarum) implements this contract.
 */
import type * as THREE from 'three';
import type { WallOpening } from '../domain/framing/generateFramedWall';
import type { FoundationData } from '../domain/foundation/calculateFoundation';
import type { SiteConditions } from '../domain/foundation/siteConditions';
import type { WeightResult } from '../domain/weight/calculateWeight';
import type { ElectricalValidationResult } from '../domain/electrical/validateElectricalWiring';
import type { RuleOverrides } from '../data/ruleRegistry';
import type { VentilationResult } from '../domain/ventilation/calculateVentilationStrategy';
import type { ErgonomicsResult } from '../domain/ergonomics/calculateBenchErgonomics';
import type { HeaterSizeMm, InteriorLayout, InteriorRoom } from '../domain/interior/types';
import type { BenchMaterialsResult } from '../domain/interior/deriveBenchMaterials';
import type { SeatingCapacityResult } from '../domain/interior/calculateSeatingCapacity';
import type { InteriorLayoutValidationResult } from '../domain/interior/validateInteriorLayout';
import type { RoofJoistResult } from '../domain/snowzone/calculateRoofJoists';
import type { GlassPenaltyResult } from '../domain/heater/validateGlassPenalty';
import type { HeaterClearanceResult } from '../domain/heater/calculateHeaterClearance';
import type { HeaterRequirementsResult } from '../domain/heater/calculateHeaterRequirements';
import type { IndoorRoomConfig } from '../domain/indoor/indoorRoom';
import type { BuildGuideStepModifier } from '../domain/build/buildGuideDraft';
import type { ClimateProfile, LocationMode, Market } from '../domain/location/types';
import type { FinishConfig, InteriorWoodSpecies } from '../domain/finishes/types';
import type { FinishPricingBasis } from '../domain/finishes/finishPriceDeltas';
import type { PremiumDetailPlacement } from './premiumDetails';
import type { DirtyFlag } from './dirtyTracking';
import type { FrontVisibilityGroups } from './frontVisibility';
import type { InteriorRenderPlan } from './interiorRenderPlan';
import type {
    EngineAuditMetadata,
    EngineEvidenceRowConfig,
} from './auditTypes';

export type HeaterType = 'wood' | 'electric';
export type MaterialPhase = 'foundation' | 'framing' | 'deck' | 'roofExterior' | 'interior' | 'systems';
export type MaterialViewMode = 'all' | 'phase';
export type RoofKind = 'mono' | 'gable' | 'barrel';
export type RoofSystemId = 'felt_roof' | 'sheet_metal';
export type RoofRunoffId = 'free_drip' | 'gutter_kit';
export type DeckShape = 'rectangular' | 'l-shape' | 'wrap-front' | 'wrap-sides';
export type DeckAttachWall = 'front' | 'back' | 'left' | 'right';
export type DeckBoardType = '28x120' | '22x95';
export type DeckJoistDimension = '45x145' | '45x170';
export type TwoRoomEntryWall = 'short' | 'long';
export type TwoRoomGlazingStyle = 'standard' | 'panorama';
export type TwoRoomSaunaPosition = 'left' | 'right';

export interface PitchedRoofConfig {
    kind: Exclude<RoofKind, 'barrel'>;
    pitchDeg: number;
    systemId: RoofSystemId;
    runoff: RoofRunoffId;
    /**
     * Mono only: the wall the pulpettak is HIGH at, in the building's local
     * frame (it turns with `rotationDeg`). Absent = `front`, the historical
     * shape, so older designs, share links and snapshots are unchanged. A
     * left/right high wall makes the roof fall across the width, with the
     * eaves along the long walls and the front/back end walls raked.
     */
    highWall?: 'front' | 'back' | 'left' | 'right';
}

export interface BarrelRoofConfig {
    kind: 'barrel';
    pitchDeg: 0;
}

export type RoofConfig = PitchedRoofConfig | BarrelRoofConfig;

export interface RoofAssemblyResult {
    status: 'complete' | 'warning' | 'manual_review';
    kind: RoofKind;
    systemId?: RoofSystemId;
    runoff?: RoofRunoffId;
    slopedAreaSqm: number;
    layers: string[];
    requiredAccessories: string[];
    missingItems: string[];
    notes: string[];
}

// ─── Shared Types ───────────────────────────────────────────
export interface SaunaDimensions {
    width: number;   // utvändig bredd (m)
    length: number;  // utvändig längd (m)
}

export interface VisibilityFlags {
    foundation: boolean;
    framing: boolean;
    panel: boolean;
    roof: boolean;
    frontWall: boolean;
    interior: boolean;
    exterior: boolean;
    deck: boolean;
    /** Heater safety-clearance overlay (inspection layer, default off). */
    heaterClearance: boolean;
    /**
     * Ventilation analysis layer (default off). The vents themselves are
     * always built and always visible — this flag switches their faces from
     * real hardware to the blue-supply / red-exhaust diagram coding.
     */
    ventilation: boolean;
}

export interface MaterialRow {
    woodSpecies?: InteriorWoodSpecies;
    material: string;
    dimension: string;
    quantity: string;
    code?: string;
    category?: string;
    phase?: MaterialPhase;
    /** Stable pricing rule binding used by baseline pricing. */
    priceRuleId?: string;
    /** Machine-stable key for test matching and i18n lookups (e.g. 'foundation.beam'). */
    materialKey?: string;
}

export interface CutRow {
    woodSpecies?: InteriorWoodSpecies;
    /** Canonical cut quantities; display text is never parsed when these exist. */
    pieces?: Array<{ lengthMm: number; count: number }>;
    part: string;
    count: string;
    cutLength: string;
    /** Machine-stable key for test matching (e.g. 'framing.studs.front'). */
    materialKey?: string;
    /**
     * Normalized lumber cross-section for purchase planning (e.g. '45x145').
     * Set when the part name alone would map to the wrong (or no) dimension —
     * takes precedence over the keyword table in dimensionFromCutRow.
     */
    purchaseDimension?: string;
    /**
     * The section is a designer's to size (glulam past the span table, issue
     * 170). The purchase plan leaves the row out rather than guess a board;
     * the Cut pane lists it under separate planning.
     */
    engineerSized?: boolean;
}

export interface ThermalInputs {
    glassAreaSqm: number;
    stoneAreaSqm: number;
    isOutdoorLogCabin: boolean;
    heaterType: HeaterType;
    /**
     * Catalog model (`domain/heater/heaterCatalog`, issue 159). Its box,
     * safety distances, kW and price replace the type defaults; absent, or a
     * model of the other type, behaves exactly as before.
     */
    heaterModelId?: string | null;
    includesShower: boolean;
    /** Undefined preserves the existing single-armature electrical allowance. */
    saunaLighting?: "wall" | "none";
    heaterPlacementZMm?: number;
}

export interface DeckConfig {
    shape: DeckShape;
    attachWall: DeckAttachWall;
    depth: number;
    width: number;
    armDepth?: number;
    armWidth?: number;
    wrapDepth?: number;
    boardType: DeckBoardType;
    joistDimension: DeckJoistDimension;
}

export interface DeckCalculationResult {
    areaSqm: number;
    perimeterM: number;
    boards: {
        count: number;
        lengthMm: number;
        dimensionLabel: string;
        ccMm: number;
        totalLpm: number;
    };
    /** Kantbräda along the open deck edges (not against the sauna). */
    fascia: {
        count: number;
        lengthMm: number;
        dimensionLabel: string;
        heightMm: number;
        thicknessMm: number;
        totalLpm: number;
    };
    joists: {
        count: number;
        lengthMm: number;
        dimensionLabel: string;
        ccMm: number;
        totalLpm: number;
    };
    bearers: {
        count: number;
        lengthMm: number;
        dimensionLabel: string;
        totalLpm: number;
    };
    piers: {
        count: number;
        dimensionLabel: string;
        layout: Array<{ x: number; z: number }>;
    };
    noggings: {
        count: number;
        lengthMm: number;
        totalLpm: number;
    };
    screws: {
        count: number;
        type: string;
    };
    materials: MaterialRow[];
    cuts: CutRow[];
    estimatedCostSek: number;
}

export interface TwoRoomConfig {
    entryWall: TwoRoomEntryWall;
    glazingStyle: TwoRoomGlazingStyle;
    saunaPosition: TwoRoomSaunaPosition;
    hasRelaxRoom: boolean;
}

export type SaunaSiting = "outdoor" | "indoor";

export interface BuildInput {
    dimensions: SaunaDimensions;
    openings: WallOpening[];
    partitionSplit?: number;
    entryWall?: TwoRoomEntryWall;
    glazingStyle?: TwoRoomGlazingStyle;
    saunaPosition?: TwoRoomSaunaPosition;
    hasRelaxRoom?: boolean;
    deckConfig?: DeckConfig | null;
    roofConfig?: RoofConfig;
    thermalInputs: ThermalInputs;
    siteConditions?: SiteConditions;
    /**
     * User-authored interior layout (lavar + aggregat).
     *
     * `null`/absent means "no layout authored yet", and the engines generate the
     * default two-tier layout instead — which is what keeps every design saved
     * before issue 130 loading unchanged. Deliberately NOT a required field: the
     * difference between "the user chose this" and "we generated it" is the
     * difference between a design tool and a configurator, and only the former
     * belongs in saved state.
     */
    interiorLayout?: InteriorLayout | null;
    /**
     * Karaktärsval — interior wood species, interior panel direction, exterior
     * treatment (issue 134).
     *
     * Absent means `DEFAULT_FINISHES`, which reproduces the pre-134 look and
     * the pre-134 geometry exactly; every consumer resolves it through
     * `normalizeFinishes`/`DEFAULT_FINISHES` rather than assuming presence, so
     * a design saved before the column existed still builds.
     */
    finishes?: FinishConfig;
    /**
     * Wall height in metres (issue 155). Absent means the rule default
     * (2.1 m), which is what every design saved before the field existed
     * builds with. Read it through `resolveWallHeightM`, never directly.
     */
    wallHeightM?: number;
    /**
     * Where the sauna stands (issue 156). Absent means `"outdoor"`: a
     * free-standing building on its own foundation, which is every design
     * saved before the field existed. `"indoor"` is a room built inside an
     * existing building — no foundation, facade or roof covering, and no
     * bygglov, snow load or ground drainage. Set by the `inomhus` engine
     * wrapper, never stored: the sauna type already carries it.
     */
    siting?: SaunaSiting;
    /**
     * The existing room an indoor sauna stands in (issues 174/175): which of
     * its walls stand against the room's walls, and how much house is above
     * it. Only read when `siting` is `"indoor"`. Absent = `DEFAULT_INDOOR_ROOM`.
     */
    indoorRoom?: IndoorRoomConfig;
    market?: Market;
    locationMode?: LocationMode;
    /** Admin rule overrides (optional — defaults used when absent) */
    overrides?: RuleOverrides;
    /** Swedish postal code for snow zone lookup (optional) */
    postalCode?: string;
    /** Manual climate class for planner-only global mode */
    climateProfile?: ClimateProfile;
}

export interface PermitRequirementsResult {
    /**
     * True for a sauna built inside an existing building (issue 156): the
     * building rules for a free-standing komplementbyggnad — area tiers, 4.5 m
     * to the boundary, strandskydd — do not apply, and the UI says so rather
     * than showing a tier.
     */
    notApplicable?: boolean;
    permitLabel: string;
    status: 'ok' | 'warning' | 'critical';
    areaSqm: number;
    requiresFireplaceNotification: boolean;
    messages: string[];
    checklist: string[];
    warningEntries?: PermitWarningEntry[];
    panoramaGlass?: PermitPanoramaGlassResult;
}

export type PermitWarningKey =
    | 'domain_permit_panorama_glassPercentExceeded'
    | 'domain_permit_panorama_safetyGlassRequired';

export type PermitWallLabelKey =
    | 'domain_permit_wallFront'
    | 'domain_permit_wallBack'
    | 'domain_permit_wallLeft'
    | 'domain_permit_wallRight';

export interface PermitWarningEntry {
    warningKey: PermitWarningKey;
    severity: 'warning' | 'critical';
    message: string;
    args: Record<string, string | number>;
    /**
     * Translation key for the wall the warning concerns. `args.wall` keeps the
     * raw enum for programmatic consumers; anything rendering the message must
     * interpolate `t[wallLabelKey]`, never the enum (see the args-cannot-nest-
     * keys note).
     */
    wallLabelKey: PermitWallLabelKey;
}

export interface PermitPanoramaWallAssessment {
    wall: 'front' | 'back' | 'left' | 'right';
    wallAreaSqm: number;
    glassAreaSqm: number;
    glassPercent: number;
    maxPercent: number;
    requiresSafetyGlass: boolean;
}

export interface PermitPanoramaGlassResult {
    maxPercentPerWall: number;
    safetyGlassThresholdSqm: number;
    uValue: number;
    wallAssessments: PermitPanoramaWallAssessment[];
}

export interface BuildTimelineStep {
    id: string;
    label: string;
    hours: number;
    weekendLabel: string;
    leadTimeWeeks?: {
        min: number;
        max: number;
    };
    weatherCritical?: boolean;
    weatherCriticalReason?: string;
    /**
     * Wait time, in hours, that this step imposes before the build can move on
     * — currently only the facade treatment's drying.
     *
     * Deliberately NOT added to `hours` or to `totalHours`: those mean *work*,
     * and folding a 24 h cure into them would tell someone they owe another
     * three weekends of labour. The UI shows it beside the step instead.
     */
    dryingHours?: number;
}

export interface BuildTimelineResult {
    totalHours: number;
    weekendEstimate: number;
    totalLeadTimeWeeks?: {
        min: number;
        max: number;
    };
    calendarEstimateLabel?: string;
    steps: BuildTimelineStep[];
}

export interface DrainageResult {
    status: 'ok' | 'warning';
    requiresFloorDrain: boolean;
    slopeRule: string;
    pipeRecommendation: string;
    foundationAdvice: string[];
    instructionLabel: string;
}

export interface ProductRecommendation {
    category: 'heater' | 'chimney' | 'door' | 'lighting';
    /** Swedish name; also the English one unless `nameEn` is set. */
    name: string;
    nameEn?: string;
    /** Swedish description. */
    description: string;
    descriptionEn: string;
    priceSek: number;
    imageUrl?: string;
    affiliateUrl: string;
    heaterTypes: HeaterType[];
    kwRange?: [number, number];
}

export interface ProductRecommendationsResult {
    items: ProductRecommendation[];
}

export interface ChimneyResult {
    diameterMm: number;
    heightMm: number;
    recommendedSystem: string;
    clearanceRule: string;
    maintenanceNote: string;
    /**
     * Indoor sauna (issue 175): how the height was reached through the house,
     * and whether the house height was assumed. Absent outdoors.
     */
    throughHouseNote?: string;
    materials: MaterialRow[];
}

export interface CableSpecResult {
    cableType: string;
    fuseLabel: string;
    /** Translation key for the fuse label — resolves via t[fuseLabelKey] */
    fuseLabelKey:
        | 'domain_cable_fuse_1x10A'
        | 'domain_cable_fuseLightingGroup'
        | 'domain_cable_fuse_3x10A'
        | 'domain_cable_fuse_3x16A'
        | 'domain_cable_fuse_3x20A'
        | 'domain_cable_fuse_3x25A';
    rcdLabel: string;
    /** Translation key for the RCD label */
    rcdLabelKey: 'domain_cable_rcd' | 'domain_cable_rcdObligatory';
    /** Translation key for the cable type; `{size}` comes from cableTypeArgs. */
    cableTypeKey: 'domain_cable_typeWoodBell' | 'domain_cable_typeFeed';
    cableTypeArgs?: { size: string };
    sihfLengthM: number;
    totalCableLengthM: number;
    routing?: CableRoutingResult;
    warnings: string[];
    /** Translation keys for all warnings — resolves via t[key] */
    warningKeys: Array<'domain_cable_warningWood' | 'domain_cable_warningLargeUnit'>;
    materials: MaterialRow[];
}

export type ElectricalPlacementWall = 'front' | 'left' | 'right';

export interface CableRoutingResult {
    entryWall: TwoRoomEntryWall;
    saunaPosition: TwoRoomSaunaPosition;
    hasRelaxRoom: boolean;
    routeLabel: 'short-wall-legacy' | 'long-wall-left-sauna' | 'long-wall-right-sauna';
    feedCableLengthM: number;
    sihfLengthM: number;
    relaxZoneCableLengthM: number;
    doorLightWall: ElectricalPlacementWall;
    outletWall: ElectricalPlacementWall;
}

export interface MaterialPhaseGroup {
    phase: MaterialPhase;
    title: string;
    rows: MaterialRow[];
    estimatedCostSek: number;
}

export type PricingAllowanceId =
    | 'delivery-rounding'
    | 'hidden-supplies'
    | 'professional-services'
    | 'market-packaging';

/**
 * Pricing copy is emitted as translation keys — one key per allowance variant —
 * and resolved at the UI boundary. Never put a market-specific string here.
 */
export type PricingAllowanceLabelKey =
    | 'domain_pricing_allowanceDeliveryRoundingLabel'
    | 'domain_pricing_allowanceHiddenSuppliesLabel'
    | 'domain_pricing_allowanceProfessionalServicesLabel'
    | 'domain_pricing_allowanceMarketPackagingLabel';

export type PricingAllowanceNoteKey =
    | 'domain_pricing_allowanceDeliveryRoundingNote'
    | 'domain_pricing_allowanceHiddenSuppliesNote'
    | 'domain_pricing_allowanceProfessionalServicesNote'
    | 'domain_pricing_allowanceMarketPackagingNote';

export interface PricingSummaryAllowance {
    id: PricingAllowanceId;
    labelKey: PricingAllowanceLabelKey;
    amountSek: number;
    noteKey: PricingAllowanceNoteKey;
}

export interface PricingSummary {
    materialCostSek: number;
    diyBudgetSek: number;
    marketPackageSek: number;
    professionalCostSek: number;
    allowances: PricingSummaryAllowance[];
    comparatorNoteKey: 'domain_pricing_comparatorNote';
}

export interface ProfessionalGuidanceStep {
    stepId: string;
    label: string;
    requiresProfessional: boolean;
    professionalType?: string;
    reason: string;
    estimatedCostSek?: number;
}

export interface ProfessionalGuidanceResult {
    steps: ProfessionalGuidanceStep[];
    professionalStepCount: number;
    estimatedTotalCostSek: number;
}

export interface PlacementRulesResult {
    /** Indoor sauna (issue 156): no plot, so no boundary or building distance. */
    indoor?: boolean;
    boundaryDistanceM: number;
    buildingDistanceM: number;
    tips: string[];
    checklist: string[];
}

export interface PartitionWallSupportSide {
    wall: 'left' | 'right';
    alignedToStud: boolean;
    studCenterMm?: number;
    bayStartMm?: number;
    bayEndMm?: number;
    blockingLengthMm?: number;
}

export interface PartitionWallFramingResult {
    status: 'direct-stud-anchor' | 'requires-blocking';
    dividerPositionMm: number;
    studWidthMm: number;
    requiredBlockingCount: number;
    sides: PartitionWallSupportSide[];
    materials: MaterialRow[];
    cuts: CutRow[];
}

export interface CameraPreset {
    id: 'hero' | 'side' | 'interior-peek';
    /**
     * Distinguishes two framings that share an `id` (the assembly film's
     * close foundation hero vs its wide building hero). The viewer only
     * re-aims the camera when `id` or `framing` changes, never merely because
     * the preset was recomputed after a design edit.
     */
    framing?: string;
    /** Open the divider when it blocks this view's hot-room sightline. */
    hideDivider?: boolean;
    label: string;
    position: THREE.Vector3;
    target: THREE.Vector3;
}

export interface RawCalculationResult {
    materials: MaterialRow[];
    cuts: CutRow[];
    weight?: WeightResult;
    structuralRoofJoists?: RoofJoistResult;
    foundation?: FoundationData;
    partitionWallFraming?: PartitionWallFramingResult;
    config?: TwoRoomConfig;
    /**
     * The layout the engine actually billed for — the user's, or the generated
     * default. Carried on the raw result so the enrichment chain validates and
     * counts capacity for the SAME layout the BOM was derived from, instead of
     * resolving a second one.
     */
    interiorLayout?: InteriorLayout;
    /**
     * The two engine-owned numbers the karaktärsval need and a BOM row cannot
     * supply (issue 134):
     *
     * - `facadeAreaSqm` — the wall area the exterior treatment's litre count is
     *   derived from. Reading it back out of the vindskydd row's `m²` string
     *   would work today and break the day that row gains a waste margin.
     * - `alternateBattenLpm` — the interior glespanel lpm the OTHER panel
     *   direction would buy. Standing panel needs lying battens (rows × wall
     *   length) and lying panel needs standing ones (stud lines × wall height);
     *   those differ, so the price delta on the direction swatch is real money
     *   and has to come from the same geometry the BOM used.
     *
     * Absent on engines with neither (the barrel has no ventilated facade and
     * no interior glespanel) — consumers must treat it as optional.
     */
    finishBasis?: FinishPricingBasis;
}

interface CalculationConditionalSlices {
    constructionBasis?: import("../domain/construction/constructionBasis").ConstructionBasis;
    openingValidation?: import("../domain/openings/validateOpeningSet").OpeningValidationResult;
    /**
     * Interior-layout slices. Absent for the barrel engine only — a tunnbastu
     * keeps its generated interior (issue 071), so there is no layout object to
     * validate or count seats on. Genuinely conditional, like `heaterClearance`.
     */
    interiorLayout?: InteriorLayout;
    /**
     * The room rectangles and heater footprint the layout above was measured in.
     *
     * Carried so a consumer can resolve the layout into the runs that were
     * actually BILLED — a run beside the aggregat is clipped, and the stored
     * layout deliberately keeps the user's full-length run instead of baking
     * that in. The material audit needs it to count bench timber from the same
     * `BenchRun` data the geometry is drawn from (issue 131); without it the
     * audit would measure unclipped runs and disagree with the BOM on exactly
     * the designs where clipping matters.
     */
    interiorMeasurements?: {
        rooms: InteriorRoom[];
        heaterSize: HeaterSizeMm;
    };
    interiorLayoutValidation?: InteriorLayoutValidationResult;
    seatingCapacity?: SeatingCapacityResult;
    deck?: DeckCalculationResult;
    chimney?: ChimneyResult;
    roofAssembly?: RoofAssemblyResult;
    /** Absent for the barrel engine (no clearance layout derivable). */
    heaterClearance?: HeaterClearanceResult;
    /**
     * Snow-zone joist sizing. Absent for the barrel engine — a tunnbastu's
     * curved staves ARE its roof, so there are no takbjälkar to dimension and
     * presenting a 45×N advisory for one is misleading (issue #49 item 5).
     */
    roofJoists?: RoofJoistResult;
    /**
     * `roofJoists` with the "limträbalk krävs" zero-depth case substituted for a
     * buildable default, for BOM/geometry consumers. Absent whenever
     * `roofJoists` is.
     */
    structuralRoofJoists?: RoofJoistResult;
}

interface CalculationEnrichmentSlices {
    volume: number;
    heaterRequirements: HeaterRequirementsResult;
    electricalBom: ElectricalValidationResult;
    ventilation: VentilationResult;
    ergonomics: ErgonomicsResult;
    glassPenalty: GlassPenaltyResult;
    permit: PermitRequirementsResult;
    timeline: BuildTimelineResult;
    drainage: DrainageResult;
    productRecommendations: ProductRecommendationsResult;
    cableSpec: CableSpecResult;
    materialsByPhase: MaterialPhaseGroup[];
    professionalGuidance: ProfessionalGuidanceResult;
    placement: PlacementRulesResult;
    pricingSummary: PricingSummary;
}

export type CalculationResultDraft = RawCalculationResult
    & Partial<CalculationConditionalSlices>
    & Partial<CalculationEnrichmentSlices>;

export type CalculationResult = Omit<RawCalculationResult, 'structuralRoofJoists'>
    & CalculationConditionalSlices
    & CalculationEnrichmentSlices;

export function isCalculationResult(result: CalculationResultDraft): result is CalculationResult {
    return !!(
        typeof result.volume === 'number'
        && result.heaterRequirements
        && result.electricalBom
        && result.ventilation
        && result.ergonomics
        && result.glassPenalty
        && result.permit
        && result.timeline
        && result.drainage
        && result.productRecommendations
        && result.cableSpec
        && result.materialsByPhase
        && result.professionalGuidance
        && result.placement
        && result.pricingSummary
    );
}

export interface BuildResult {
    saunaGroup: THREE.Group;
    foundationGroup: THREE.Group;
    framingGroup: THREE.Group;
    panelGroup: THREE.Group;
    roofGroup: THREE.Group;
    interiorGroup: THREE.Group;
    exteriorGroup: THREE.Group;
    deckGroup: THREE.Group;
    frontVisibilityGroups: FrontVisibilityGroups;
    premiumPlacements: PremiumDetailPlacement[];
}

// ─── Engine Interface ───────────────────────────────────────

/**
 * Internal engine interface used by individual sauna engines.
 * These engines return RawCalculationResult — the enrichment pipeline
 * in index.ts wraps them to produce the fully-typed CalculationResult.
 */
export interface RawSaunaEngine
    extends Omit<
        SaunaEngine,
        | 'calculateMaterials'
        | 'getEvidenceConfig'
        | 'buildSauna3D'
        | 'buildSubsystem'
        | 'getCameraPresets'
    > {
    /**
     * `benchFocusM` is the plan-view centre of the lavar the layout places, so
     * the interior preset aims at the benches instead of at a hard-coded
     * fraction of the building. Supplied by the registry, which is where the
     * layout is resolved.
     */
    getCameraPresets?: (
        input: BuildInput,
        benchFocusM?: { xM: number; zM: number } | null,
    ) => CameraPreset[];
    /**
     * `bench` carries the layout-backed bench rows. The engine modules sit below
     * `heaterClearanceLayout.ts` in the import graph and cannot resolve the
     * interior layout themselves, so the registry resolves it once and hands the
     * derived rows down. Optional so a direct unit-test call still compiles;
     * every production path supplies it.
     */
    calculateMaterials(input: BuildInput, bench?: BenchMaterialsResult): RawCalculationResult;
    /**
     * `interior` is that same layout resolved into geometry (issue 131). It
     * arrives the same way and for the same import-graph reason as `bench`: the
     * two parameters are the material list and the model reading ONE
     * description of the interior. Omitted → the engine generates the #130
     * default itself, through the same layout code path rather than the fixed
     * geometry it used to carry.
     */
    buildSauna3D(input: BuildInput, interior?: InteriorRenderPlan): BuildResult;
    buildSubsystem?: (
        subsystem: DirtyFlag,
        input: BuildInput,
        ctx: unknown,
        interior?: InteriorRenderPlan,
    ) => THREE.Group;
    getEvidenceConfig(input: BuildInput, calculation: CalculationResult | RawCalculationResult): EngineEvidenceRowConfig[];
}

export interface SaunaEngine {
    /** Human-readable label for the sidebar header */
    label: string;
    /** Emoji for the type */
    icon: string;
    /** Short description */
    description: string;
    /** Default dimensions */
    defaultDimensions: SaunaDimensions;
    /** Build the 3D model */
    buildSauna3D(input: BuildInput): BuildResult;
    /** Calculate material + cut lists */
    calculateMaterials(input: BuildInput): CalculationResult;
    /** Audit metadata for visual coverage and generic material categorization */
    getAuditMetadata(): EngineAuditMetadata;
    /** Explicit, engine-owned audit evidence rows */
    getEvidenceConfig(input: BuildInput, calculation: CalculationResult): EngineEvidenceRowConfig[];
    /** Get default camera position */
    getDefaultCameraPosition(W: number, L: number): {
        position: THREE.Vector3;
        target: THREE.Vector3;
    };
    /** Curated camera presets for the live viewer */
    getCameraPresets?: (input: BuildInput) => CameraPreset[];
    /** Optional engine-owned build-guide step transform applied before serialization. */
    modifyBuildGuideSteps?: BuildGuideStepModifier;

    /**
     * Optional: rebuild a single subsystem group.
     * Engines that implement this enable selective geometry updates
     * (dirty-node system) instead of full rebuilds via buildSauna3D.
     */
    buildSubsystem?: (
        subsystem: DirtyFlag,
        input: BuildInput,
        ctx: unknown,
    ) => THREE.Group;

    /**
     * Create a shared build context that is reusable across multiple
     * buildSubsystem calls within the same rebuild frame.
     */
    createBuildContext?: (input: BuildInput) => unknown;
}
